import { cssVar } from '@nautilusbio/nb-design-system/styles/css-variables';
import { Text } from '@nautilusbio/nb-design-system/Text';
import { Tooltip } from '@nautilusbio/nb-design-system/Tooltip';
import { Link } from '@tanstack/react-router';
import { clsx } from 'clsx';
import { DynamicIcon } from 'lucide-react/dynamic';
import type { IconName } from 'lucide-react/dynamic';
import React, { memo, useMemo } from 'react';
import type { FC } from 'react';

import { SIDEBAR_ICON_CONFIG } from '../constants';
import styles from '../styles.module.css';
import type { IconComponent, SidebarLinkProps } from '../types';

// Static tooltip props
const TOOLTIP_PROPS = {
  offset: 2,
  placement: 'right' as const,
  size: 'lg' as const,
};

export type NavigationLinkProps = {
  /** Link configuration including path and label */
  link: SidebarLinkProps;
  /** Whether the sidebar is currently collapsed */
  isSidebarCollapsed: boolean;
};

/**
 * Custom comparison function for NavigationLink memoization
 * Returns true if props are equal (skip re-render), false otherwise
 */
const arePropsEqual = (prevProps: NavigationLinkProps, nextProps: NavigationLinkProps): boolean => {
  // Check primitive props
  if (
    prevProps.isSidebarCollapsed !== nextProps.isSidebarCollapsed ||
    prevProps.link.preload !== nextProps.link.preload ||
    prevProps.link.to !== nextProps.link.to ||
    prevProps.link.children !== nextProps.link.children ||
    prevProps.link.icon !== nextProps.link.icon ||
    prevProps.link.onClick !== nextProps.link.onClick
  ) {
    return false;
  }

  // Deep compare roles array (same reference = equal, but do NOT short-circuit before checking subLinks)
  const prevRoles = prevProps.link.roles;
  const nextRoles = nextProps.link.roles;

  if (prevRoles !== nextRoles) {
    if (!prevRoles || !nextRoles) return false; // One is undefined/null, other isn't
    if (prevRoles.length !== nextRoles.length) return false; // Different lengths
    if (!prevRoles.every((role, index) => role === nextRoles[index])) return false;
  }

  // Deep compare subLinks array
  const prevSubLinks = prevProps.link.subLinks;
  const nextSubLinks = nextProps.link.subLinks;

  if (prevSubLinks === nextSubLinks) return true; // Both same reference or both undefined
  if (!prevSubLinks || !nextSubLinks) return false; // One is undefined/null, other isn't
  if (prevSubLinks.length !== nextSubLinks.length) return false; // Different lengths

  // Shallow comparison of subLinks (reference equality)
  return prevSubLinks.every((subLink, index) => subLink === nextSubLinks[index]);
};

/**
 * Individual navigation link component for the sidebar
 * Memoized to prevent unnecessary re-renders when parent re-renders
 */
const NavigationLink: FC<NavigationLinkProps> = memo(({ link, isSidebarCollapsed }) => {
  const { children, icon, ...linkProps } = link;

  // Consolidate all computed values that depend on isSidebarCollapsed
  // Using a single useMemo is more efficient than multiple hooks
  const config = useMemo(() => {
    const iconConfig = isSidebarCollapsed ? SIDEBAR_ICON_CONFIG.collapsed : SIDEBAR_ICON_CONFIG.expanded;

    return {
      iconSize: iconConfig.size,
      iconStrokeWidth: iconConfig.strokeWidth,
      linkClassName: clsx(styles.sidebarLink, isSidebarCollapsed && styles.sidebarLinkIsCollapsed),
      iconClassName: isSidebarCollapsed ? styles.iconIsCollapsed : undefined,
      tooltipTriggerClassName: clsx(styles.tooltip, !isSidebarCollapsed && styles.tooltipExpanded),
      isTooltipDisabled: !isSidebarCollapsed,
    };
  }, [isSidebarCollapsed]);

  // Check if icon is a component or a string (lucide icon name)
  const isIconComponent = typeof icon !== 'string';
  const ResolvedIcon = isIconComponent ? (icon as IconComponent) : null;
  const iconName = !isIconComponent ? (icon as IconName) : null;

  return (
    <li className={styles.sidebarItem}>
      <Tooltip
        {...TOOLTIP_PROPS}
        zIndex={cssVar.zIndex.alwaysTop}
        content={children}
        label={typeof children === 'string' ? children : undefined}
        isDisabled={config.isTooltipDisabled}
        trigger={
          <Link {...linkProps} className={config.linkClassName} data-testid={`link${linkProps.to}`}>
            {ResolvedIcon ? (
              <ResolvedIcon
                className={config.iconClassName}
                size={config.iconSize}
                strokeWidth={config.iconStrokeWidth}
              />
            ) : (
              <DynamicIcon
                className={config.iconClassName}
                name={iconName as IconName}
                size={config.iconSize}
                strokeWidth={config.iconStrokeWidth}
              />
            )}

            {!isSidebarCollapsed && <Text as="span">{children}</Text>}
          </Link>
        }
        triggerClassName={config.tooltipTriggerClassName}
      />
    </li>
  );
}, arePropsEqual);

NavigationLink.displayName = 'NavigationLink';

export default NavigationLink;
